// A quién va el correo: familia de público + filtros, y el conteo en vivo.
import React from 'react';
import { Alert, Radio, Select, Skeleton } from 'antd';
import { ORIGENES } from './textos';

export const segmentoVacio = (tipo) => {
  if (tipo === 'matriculados') return { tipo, estado: 'activa' };
  if (tipo === 'clientes') return { tipo };
  return { tipo: 'inscritos', vigencia: 'activas' };
};

const Campo = ({ label, children, ayuda }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
    {children}
    {ayuda && <span className="block text-xs text-gray-400 mt-1">{ayuda}</span>}
  </label>
);

export function SelectorPublico({ segmento, onChange, opciones }) {
  const alcance = opciones?.alcance || {};
  const talleres = opciones?.talleres || [];
  const taller = talleres.find((t) => t._id === segmento.taller);
  const set = (cambios) => onChange({ ...segmento, ...cambios });

  return (
    <div className="space-y-4">
      <Radio.Group
        value={segmento.tipo}
        onChange={(e) => onChange(segmentoVacio(e.target.value))}
        className="flex flex-col gap-2"
      >
        <Radio value="inscritos" disabled={!alcance.inscritos}>Inscritos en talleres</Radio>
        <Radio value="matriculados" disabled={!alcance.matriculados}>Matriculados de un recinto</Radio>
        <Radio value="clientes" disabled={!alcance.clientes}>Todos los clientes con cuenta</Radio>
      </Radio.Group>

      {segmento.tipo === 'inscritos' && (
        <div className="space-y-3">
          <Campo label="Taller" ayuda={!segmento.taller ? 'Sin taller: se usan los filtros de recinto y tipo de actividad.' : null}>
            <Select
              showSearch
              allowClear
              className="w-full"
              placeholder="Todos los talleres"
              value={segmento.taller || undefined}
              optionFilterProp="label"
              options={talleres.map((t) => ({ value: t._id, label: `${t.nombre}${t.recintoNombre ? ` · ${t.recintoNombre}` : ''}` }))}
              onChange={(v) => set({ taller: v || undefined, horario: undefined })}
            />
          </Campo>
          {taller && taller.horarios.length > 1 && (
            <Campo label="Horario">
              <Select
                allowClear
                className="w-full"
                placeholder="Todos los horarios"
                value={segmento.horario || undefined}
                options={taller.horarios.map((h) => ({ value: h._id, label: h.etiqueta }))}
                onChange={(v) => set({ horario: v || undefined })}
              />
            </Campo>
          )}
          {!segmento.taller && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Campo label="Recinto">
                <Select
                  allowClear
                  className="w-full"
                  placeholder="Todos"
                  value={segmento.recinto || undefined}
                  options={(opciones?.recintos || []).map((r) => ({ value: r._id, label: r.nombre }))}
                  onChange={(v) => set({ recinto: v || undefined })}
                />
              </Campo>
              <Campo label="Tipo de actividad">
                <Select
                  allowClear
                  className="w-full"
                  placeholder="Todas"
                  value={segmento.tipoActividad || undefined}
                  options={(opciones?.tiposActividad || []).map((t) => ({ value: t, label: t }))}
                  onChange={(v) => set({ tipoActividad: v || undefined })}
                />
              </Campo>
            </div>
          )}
          <Campo label="Inscripciones">
            <Radio.Group value={segmento.vigencia} onChange={(e) => set({ vigencia: e.target.value })}>
              <Radio value="activas">Solo vigentes</Radio>
              <Radio value="todas">Incluir ciclos anteriores</Radio>
            </Radio.Group>
          </Campo>
        </div>
      )}

      {segmento.tipo === 'matriculados' && (
        <div className="space-y-3">
          <Campo label="Recinto">
            <Select
              allowClear
              className="w-full"
              placeholder="Todos los recintos"
              value={segmento.recinto || undefined}
              options={(opciones?.recintos || []).map((r) => ({ value: r._id, label: r.nombre }))}
              onChange={(v) => set({ recinto: v || undefined })}
            />
          </Campo>
          <Campo label="Matrículas">
            <Radio.Group value={segmento.estado} onChange={(e) => set({ estado: e.target.value })}>
              <Radio value="activa">Solo vigentes</Radio>
              <Radio value="todas">Incluir vencidas</Radio>
            </Radio.Group>
          </Campo>
        </div>
      )}

      {segmento.tipo === 'clientes' && (
        <Alert type="info" showIcon message="Todas las cuentas activas del portal, tengan o no un taller vigente." />
      )}
    </div>
  );
}

export function ResumenPublico({ conteo, cargando, error }) {
  if (error) return <Alert type="error" showIcon message={error} />;
  if (cargando && !conteo) return <Skeleton active paragraph={{ rows: 2 }} />;
  if (!conteo) return null;

  const quitados = [
    conteo.sinCorreo && `${conteo.sinCorreo} sin correo válido`,
    conteo.duplicados && `${conteo.duplicados} correos repetidos`,
    conteo.dadosDeBaja && `${conteo.dadosDeBaja} se dieron de baja`,
  ].filter(Boolean);

  return (
    <div className={cargando ? 'opacity-60 transition-opacity' : ''}>
      <div className="text-4xl font-extrabold text-gray-900 tabular-nums leading-none">{conteo.total.toLocaleString('es-CL')}</div>
      <div className="text-sm text-gray-500 mt-1">{conteo.total === 1 ? 'persona recibirá este correo' : 'personas recibirán este correo'}</div>
      {quitados.length > 0 && (
        <div className="text-xs text-gray-500 mt-2">
          De {conteo.coinciden.toLocaleString('es-CL')} que cumplen el filtro: {quitados.join(', ')}.
        </div>
      )}
      {conteo.total === 0 && <Alert className="mt-3" type="warning" showIcon message="Nadie recibiría este correo con este filtro." />}
      {conteo.muestra?.length > 0 && (
        <details className="mt-3">
          <summary className="text-sm font-semibold text-primary cursor-pointer">Ver a quién</summary>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            {conteo.muestra.map((m, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span className="truncate">{m.nombre || 'Sin nombre'}</span>
                <span className="text-xs text-gray-400 whitespace-nowrap">{ORIGENES[m.origen]}</span>
              </li>
            ))}
            {conteo.total > conteo.muestra.length && (
              <li className="text-xs text-gray-400">y {(conteo.total - conteo.muestra.length).toLocaleString('es-CL')} más</li>
            )}
          </ul>
        </details>
      )}
    </div>
  );
}
